import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const { string, number } = PropTypes;

/**
 * LazyLoad component that lazy loads children
 * @param {Object} props
 * @param {ReactElement} props.children - Component children that will be lazy loaded
 * @param {number} props.threshold - Indicates what percentage of the target's visibility the observer's callback should be executed
 * @param {string} props.rootMargin - Margin around the root
 * @param {number|string} props.height - placeholder div height
 * @param {number|string} props.width - placeholder div width
 * @param {string} props.placeholderClassname - placeholder class name (before lazy loading happens)
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API for more info on
 * `rootMargin` and `threshold`
 */
const LazyLoad = ({ children, threshold, rootMargin, height, width, placeholderClassname }) => {
  // React refs
  const observer = useRef(null);
  const el = useRef(null);

  // React state
  const [isVisible, setIsVisible] = useState(false);

  const style = { height, width };

  useEffect(() => {
    const options = {
      root: null,
      rootMargin,
      threshold,
    };

    if (window) {
      require('intersection-observer');
      observer.current = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          observer.current.unobserve(el.current);
          setIsVisible(true);
        }
      }, options);
      observer.current.observe(el.current);
    }

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, []);

  return isVisible ? children : <div ref={el} style={style} className={placeholderClassname} />;
};

LazyLoad.propTypes = {
  threshold: number,
  rootMargin: string,
  height: string,
  width: string,
  placeholderClassname: string,
};

LazyLoad.defaultProps = {
  threshold: 0.0,
  rootMargin: '50px',
  height: '100px',
  width: '100px',
  placeholderClassname: '',
};

export default LazyLoad;
